// Create a new file: /components/workflow/CustomAgentNodeModal.jsx
import React, { useState, useMemo, useRef } from 'react';
import { Bot, Edit, FileJson2 } from 'lucide-react';
import toast from 'react-hot-toast';

import SchemaViewer from '../NewWorkflow/shared/SchemaViewer';
import PromptEditor from '../NewWorkflow/shared/PromptEditor';
import SchemaBuilderModal from '../NewWorkflow/modals/SchemaBuilderModal';
import Modal from '../base/Modal';
import { fieldsToSchema, schemaToFields } from '../NewWorkflow/utils/schemaUtils';

const tiptapToBackendFormat = (json) => {
    if (!json || !json.content) return [];
    const result = [];
    json.content.forEach(paragraph => {
        if (paragraph.content) {
            paragraph.content.forEach(node => {
                if (node.type === 'text') {
                    result.push({ type: 'text', content: node.text });
                } else if (node.type === 'variablePill') {
                    result.push({
                        type: 'variable',
                        path: node.attrs.path.split('.'),
                        display: node.attrs.display,
                        // ⭐ ADD THIS: Include dataType in the backend format
                        dataType: node.attrs.dataType,
                    });
                }
            });
        }
        // After each paragraph, add a newline. This preserves line breaks.
        result.push({ type: 'text', content: '\n' });
    });
    // Remove the last newline if it's extraneous
    if (result.length > 0 && result[result.length - 1].content === '\n') {
        result.pop();
    }
    return result;
};


const CustomAgentNodeModal = ({ mode, node, nodes, edges, onSave, onDelete, onClose, globalState }) => {
    const [systemPromptContent, setSystemPromptContent] = useState(node?.data?.systemPromptContent || { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'You are a helpful assistant that only responds in JSON.' }] }] });
    const [userPromptContent, setUserPromptContent] = useState(node?.data?.userPromptContent || { type: 'doc', content: [{ type: 'paragraph' }] });
    const [outputFields, setOutputFields] = useState(node?.data?.outputFields || []);
    const [isBuilderOpen, setIsBuilderOpen] = useState(false);
    const lastActiveEditorRef = useRef(null);

    // --- NEW: Calculate output property count for the summary ---
    const outputPropertyCount = useMemo(() => {
        return outputFields.length;
    }, [outputFields]);

    const inputRootFields = useMemo(() => {
        const connectedInputNodes = edges
            .filter(edge => edge.target === node.id)
            .map(edge => nodes.find(n => n.id === edge.source))
            .filter(Boolean)
            .filter(parentNode => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
            .map(parentNode => {
                let schema;
                if (parentNode.data.jsonOutput && parentNode.data.jsonOutputSchema?.properties) {
                    schema = parentNode.data.jsonOutputSchema;
                } else {
                    schema = {
                        title: 'Simple Output', type: 'object',
                        properties: { 'output': { type: 'string', description: `Output from "${parentNode.data.label || parentNode.type}"` } }
                    };
                }
                return {
                    id: `input_${parentNode.id}`, fieldName: parentNode.data.label || 'Input Node',
                    dataType: 'Object', children: schemaToFields(schema, ''),
                };
            }).filter(Boolean);

        // ⭐ 2. Create a "virtual node" for the Global State if it exists
        const globalStateNode = [];
        if (globalState && globalState.fields.length > 0) {
            const globalSchema = fieldsToSchema(globalState.fields, "globalState");
            globalStateNode.push({
                id: 'globalState', // Special, reserved ID
                fieldName: 'Global State',
                dataType: 'Object',
                children: schemaToFields(globalSchema, ''),
            });
        }

        // ⭐ 3. Combine the Global State with the other input nodes
        return [...globalStateNode, ...connectedInputNodes];
    }, [node.id, nodes, edges]);

    // ... (rest of the logic from your sandbox AgentNodeModal: outputRootFields, handleFieldSelect, handleSchemaSave)

    const outputRootFields = useMemo(() => [{
        id: 'output',
        fieldName: 'Output',
        dataType: 'Object',
        children: outputFields
    }], [outputFields]);

    const handleFieldSelect = (fullPath, field) => {
        const rootField = inputRootFields.find(rf => fullPath.startsWith(rf.id));
        let displayPath = fullPath;
        if (rootField) {
            displayPath = (fullPath === rootField.id)
                ? rootField.fieldName
                : fullPath.replace(rootField.id, rootField.fieldName).replace(/^\./, '');
        }

        const variableToInsert = {
            type: 'variablePill',
            attrs: {
                path: fullPath,
                display: displayPath,
                // ⭐ ADD THIS: Pass the field's dataType
                dataType: field.dataType,
            }
        };

        const targetEditor = lastActiveEditorRef.current;

        if (targetEditor) {
            targetEditor.chain().focus().insertContent(variableToInsert).run();
        } else {
            toast.error("Please click into a prompt editor first to insert a variable.");
        }
    };

    const handleSchemaSave = ({ fields }) => { setOutputFields(fields); };

    const handleSaveAndClose = () => {
        const finalOutputSchema = fieldsToSchema(outputFields, "AgentOutput");
        onSave({
            ...node,
            data: {
                ...node.data,
                label: `Custom Agent (${outputFields.length} outputs)`,
                systemPromptContent,
                userPromptContent,
                systemPrompt: tiptapToBackendFormat(systemPromptContent),
                userPrompt: tiptapToBackendFormat(userPromptContent),
                outputFields,
                jsonOutputSchema: finalOutputSchema,
                jsonOutput: true,
            },
        });
        onClose();
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-0 sm:p-4" onClick={onClose}>
                <div
                    className="bg-zinc-900 w-full h-full sm:w-[95vw] sm:h-[90vh] sm:rounded-xl shadow-2xl border border-zinc-700 flex flex-col"
                    onClick={e => e.stopPropagation()}
                >
                    {/* MODAL HEADER */}
                    <div className="p-4 border-b border-zinc-700 flex justify-between items-center flex-shrink-0">
                        <h2 className="text-xl font-semibold text-white flex items-center gap-3">
                            <Bot className="text-cyan-400" />
                            <span>Configure Custom Agent</span>
                        </h2>
                        <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors text-2xl leading-none">&times;</button>
                    </div>

                    {/* MAIN WORKSPACE */}
                    <div className="flex-grow grid grid-cols-5 gap-6 p-6 overflow-hidden">

                        {/* --- LEFT COLUMN (Vertically Split) --- */}
                        <div className="col-span-2 flex flex-col gap-6 h-full overflow-hidden">

                            {/* Top Part: Available Inputs */}
                            <div className="flex flex-col gap-3 min-h-0" style={{ flexBasis: '60%' }}>
                                <div className='px-1 flex-shrink-0'>
                                    <h4 className="text-sm font-semibold text-slate-100">Available Inputs</h4>
                                    {/* <p className="text-sm text-slate-400 mt-1">Click a field to insert it into an active prompt.</p> */}
                                </div>
                                <div className="flex-grow overflow-y-auto pr-2 -mr-2">
                                    <SchemaViewer
                                        rootFields={inputRootFields}
                                        onFieldClick={handleFieldSelect}
                                    />
                                </div>
                            </div>

                            {/* Bottom Part: Output Schema */}
                            <div className="flex flex-col gap-3 min-h-0" style={{ flexBasis: '40%' }}>
                                <div className='px-1 flex-shrink-0 flex justify-between items-center'>
                                    <h4 className="text-sm font-semibold text-slate-100">Output Schema</h4>
                                    <button onClick={() => setIsBuilderOpen(true)} className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 text-sm font-semibold">
                                        <Edit size={14} />
                                        <span>Edit</span>
                                    </button>
                                </div>
                                <div className="flex-grow overflow-y-auto pr-2 -mr-2">
                                    <SchemaViewer
                                        rootFields={outputRootFields}
                                        isOutput={true}
                                    />
                                </div>
                            </div>

                        </div>

                        {/* --- RIGHT COLUMN: Prompt Editors --- */}
                        <div className="col-span-3 flex flex-col gap-6 h-full overflow-y-auto pr-2">
                            <div>
                                <label className="block text-base font-medium text-slate-200 mb-2">System Prompt</label>
                                <PromptEditor
                                    initialContent={systemPromptContent}
                                    onUpdate={setSystemPromptContent}
                                    onFocus={(editor) => { lastActiveEditorRef.current = editor; }}
                                />
                            </div>
                            <div>
                                <label className="block text-base font-medium text-slate-200 mb-2">User Prompt</label>
                                <PromptEditor
                                    initialContent={userPromptContent}
                                    onUpdate={setUserPromptContent}
                                    onFocus={(editor) => { lastActiveEditorRef.current = editor; }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* MODAL FOOTER */}
                    <div className="p-4 flex justify-between items-center bg-zinc-800/50 border-t border-zinc-700 rounded-b-lg flex-shrink-0">
                        {mode === 'edit' && node?.id && (
                            <button onClick={() => onDelete(node.id)} className="px-4 py-2 text-sm font-semibold text-red-400 bg-red-900/50 hover:bg-red-900/80 rounded-md transition-colors">
                                Delete Node
                            </button>
                        )}
                        <div className="flex-grow" />
                        <div className="flex items-center gap-3">
                            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-300 bg-zinc-700 hover:bg-zinc-600 rounded-md transition-colors">Cancel</button>
                            <button onClick={handleSaveAndClose} className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-md transition-colors">Save Changes</button>
                        </div>
                    </div>
                </div>
            </div>

            <SchemaBuilderModal
                isOpen={isBuilderOpen}
                onClose={() => setIsBuilderOpen(false)}
                onSave={handleSchemaSave}
                initialFields={outputFields}
            />
        </>
    );
};
export default CustomAgentNodeModal;